import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Panel, PanelHeader, PanelHeaderBack, Group, Div, Spinner,
  Text, Caption, Headline, Select, Snackbar,
} from '@vkontakte/vkui';
import { Icon24Cancel } from '@vkontakte/icons';
import { api } from '../../api/client';
import { useWeddingStore } from '../../stores/wedding';
import type { Task, Subtask, TaskCategory } from '../../types';

function parseDate(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

function todayIn(tz?: string): string {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: tz || 'UTC' }).format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

function isOverdue(t: { is_complete: boolean; due_date?: string | null }, tz?: string): boolean {
  return !t.is_complete && !!t.due_date && parseDate(t.due_date).getTime() < parseDate(todayIn(tz)).getTime();
}

type Stats = { total: number; done: number; pending: number; overdue: number };

function statsOf(items: { is_complete: boolean; due_date?: string | null }[], tz?: string): Stats {
  const total = items.length;
  const done = items.filter((i) => i.is_complete).length;
  return { total, done, pending: total - done, overdue: items.filter((i) => isOverdue(i, tz)).length };
}

/** Simple conic-gradient donut, no chart library. */
function Donut({ stats, size = 96 }: { stats: Stats; size?: number }) {
  const total = stats.total || 1;
  const doneDeg = (stats.done / total) * 360;
  const overdueDeg = (stats.overdue / total) * 360;
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: `conic-gradient(
          var(--vkui--color_background_positive) 0deg ${doneDeg}deg,
          var(--vkui--color_background_negative) ${doneDeg}deg ${doneDeg + overdueDeg}deg,
          var(--vkui--color_background_secondary) ${doneDeg + overdueDeg}deg 360deg
        )`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <div style={{
        width: size * 0.62, height: size * 0.62, borderRadius: '50%',
        background: 'var(--vkui--color_background_content)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Text style={{ fontWeight: 600 }}>
          {stats.total ? Math.round((stats.done / stats.total) * 100) : 0}%
        </Text>
      </div>
    </div>
  );
}

function StatRow({ label, stats }: { label: string; stats: Stats }) {
  return (
    <Div>
      <Text style={{ fontWeight: 600, marginBottom: 8 }}>{label}</Text>
      <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
        <Donut stats={stats} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 0 }}>
          <Caption>Всего: {stats.total}</Caption>
          <Caption style={{ color: 'var(--vkui--color_text_positive)' }}>Выполнено: {stats.done}</Caption>
          <Caption style={{ color: 'var(--vkui--color_text_secondary)' }}>В работе: {stats.pending}</Caption>
          <Caption style={{ color: 'var(--vkui--color_text_negative)' }}>Просрочено: {stats.overdue}</Caption>
        </div>
      </div>
    </Div>
  );
}

export default function ChecklistAnalytics() {
  const navigate = useNavigate();
  const { currentWedding, weddingsLoaded } = useWeddingStore();

  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [subs, setSubs] = useState<Subtask[]>([]);
  const [categories, setCategories] = useState<TaskCategory[]>([]);
  const [month, setMonth] = useState('all');
  const [cat, setCat] = useState('');
  const [snack, setSnack] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!currentWedding) return;
    setLoading(true);
    try {
      const [tasksRes, subsList, cats] = await Promise.all([
        api.get<{ items: Task[]; total: number }>(`weddings/${currentWedding.id}/tasks?limit=1000&sort=date`),
        api.get<Subtask[]>(`weddings/${currentWedding.id}/tasks/subtasks`),
        api.get<TaskCategory[]>(`weddings/${currentWedding.id}/tasks/categories`),
      ]);
      setTasks(tasksRes.items);
      setSubs(subsList);
      setCategories(cats);
    } catch {
      setSnack('Не удалось загрузить аналитику');
    } finally {
      setLoading(false);
    }
  }, [currentWedding]);

  useEffect(() => {
    if (!currentWedding) {
      if (weddingsLoaded) navigate('/');
      return;
    }
    void load();
  }, [currentWedding, weddingsLoaded, load, navigate]);

  /** Month buckets built from real due dates, in the style of MyWed's Month picker. */
  const monthOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of tasks) {
      if (!t.due_date) continue;
      const d = parseDate(t.due_date);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });
      map.set(key, label.charAt(0).toUpperCase() + label.slice(1));
    }
    return [{ label: 'Всё время', value: 'all' }, ...[...map.entries()].map(([value, label]) => ({ label, value }))];
  }, [tasks]);

  const filteredTasks = useMemo(() => {
    let list = tasks;
    if (cat) list = list.filter((t) => t.category_id === cat);
    if (month !== 'all') {
      list = list.filter((t) => {
        if (!t.due_date) return false;
        const d = parseDate(t.due_date);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` === month;
      });
    }
    return list;
  }, [tasks, cat, month]);

  const taskIds = useMemo(() => new Set(filteredTasks.map((t) => t.id)), [filteredTasks]);

  const filteredSubs = useMemo(() => {
    const list = subs.filter((s) => taskIds.has(s.task_id));
    return list;
  }, [subs, taskIds]);

  const taskStats = useMemo(() => statsOf(filteredTasks, currentWedding?.timezone), [filteredTasks, currentWedding?.timezone]);
  const subStats = useMemo(() => statsOf(filteredSubs, currentWedding?.timezone), [filteredSubs, currentWedding?.timezone]);

  return (
    <Panel>
      <PanelHeader before={<PanelHeaderBack onClick={() => navigate('/tasks')} />}>
        Аналитика чек-листа
      </PanelHeader>

      {loading ? (
        <Group>
          <Div style={{ textAlign: 'center', padding: 40 }}><Spinner size="l" /></Div>
        </Group>
      ) : (
        <>
          <Group>
            <Div>
              <Headline level="2">Прогресс</Headline>
              <Text style={{ marginTop: 6, color: 'var(--vkui--color_text_secondary)' }}>
                Выполнено задач: {taskStats.done} из {taskStats.total} ({taskStats.total ? Math.round((taskStats.done / taskStats.total) * 100) : 0}%)
              </Text>
              <div style={{ display: 'flex', gap: 8, marginTop: 12, overflowX: 'auto' }}>
                {filteredTasks.slice(0, 20).map((t) => (
                  <div
                    key={t.id}
                    title={`${t.name}${t.due_date ? ` — ${parseDate(t.due_date).toLocaleDateString('ru-RU')}` : ''}`}
                    style={{
                      width: 14, height: 28, borderRadius: 4, flexShrink: 0,
                      background: t.is_complete
                        ? 'var(--vkui--color_background_positive)'
                        : isOverdue(t, currentWedding?.timezone)
                          ? 'var(--vkui--color_background_negative)'
                          : 'var(--vkui--color_background_secondary)',
                    }}
                  />
                ))}
              </div>
            </Div>
          </Group>

          <Group>
            <Div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <Caption style={{ color: 'var(--vkui--color_text_secondary)' }}>Месяц</Caption>
                <Select
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                  options={monthOptions}
                />
              </div>
              <div style={{ flex: 1 }}>
                <Caption style={{ color: 'var(--vkui--color_text_secondary)' }}>Категория</Caption>
                <Select
                  value={cat}
                  onChange={(e) => setCat(e.target.value)}
                  options={[
                    { label: 'Все категории', value: '' },
                    ...categories.map((c) => ({ label: c.name, value: c.id })),
                  ]}
                />
              </div>
            </Div>
          </Group>

          <Group header={<Text>Сводка</Text>}>
            <StatRow label="Задачи" stats={taskStats} />
            <StatRow label="Подзадачи" stats={subStats} />
          </Group>

          {filteredTasks.length === 0 && (
            <Group>
              <Div style={{ textAlign: 'center', padding: 24 }}>
                <Text style={{ color: 'var(--vkui--color_text_secondary)' }}>Нет данных за выбранный период</Text>
              </Div>
            </Group>
          )}
        </>
      )}

      {snack && (
        <Snackbar onClose={() => setSnack(null)} onClosed={() => setSnack(null)} before={<Icon24Cancel />}>
          {snack}
        </Snackbar>
      )}
      <Div style={{ height: 24 }} />
    </Panel>
  );
}
